import type { CurriculumTierId, RoundPresentation, Trin } from '../../types';
import {
  fromChunks,
  fromLetters,
  literal,
  localizedFromChunks,
  localizedLiteral,
  round,
} from './generators';

export interface PairTrinPhrases {
  intro: string;
  connect: string;
  recall1: string;
  recall2: string;
  challenge: string;
}

export type PaletteKind = 'metronome' | 'echo';

export interface PairTrinOptions {
  /** Runde 3 bliver en palette cleanser (Plan C). */
  palette?: PaletteKind;
}

function paletteRound(
  id: string,
  kind: PaletteKind,
  generate: (opts: import('../../types').GenerateRoundOptions) => string,
) {
  const presentation: RoundPresentation =
    kind === 'metronome'
      ? { kind: 'metronome', bpm: 55 }
      : { kind: 'echo', flashMs: 1500, unit: 'word' };

  return round({
    id: `${id}-r3`,
    title: kind === 'metronome' ? 'Hold takten' : 'Flash-genkald',
    purpose:
      kind === 'metronome'
        ? 'Skriv én tast pr. slag — find et jævnt tempo.'
        : 'Se ordet kort, skriv det fra hukommelsen.',
    mode: 'recall',
    generate,
    presentation,
  });
}

export function pairTrin(
  tierId: CurriculumTierId,
  n: number,
  title: string,
  description: string,
  goal: string,
  newKeys: string[],
  prior: string[],
  p: PairTrinPhrases,
  opts: PairTrinOptions = {},
): Trin {
  const unlocked = [...prior, ...newKeys];
  const id = `${tierId}-trin-${n}`;
  const r3 = opts.palette
    ? paletteRound(id, opts.palette, literal(p.recall1))
    : round({
        id: `${id}-r3`,
        title: 'Rytme',
        purpose: 'Find et jævnt tempo.',
        mode: 'recall',
        generate: literal(p.recall1),
      });

  return {
    id,
    tierId,
    trinNumber: n,
    title,
    description,
    goal,
    unlockedKeys: unlocked,
    newKeys,
    rounds: [
      round({
        id: `${id}-r1`,
        title: 'Nye taster',
        purpose: 'Mærk de nye taster roligt.',
        mode: 'guided',
        generate: literal(p.intro),
      }),
      round({
        id: `${id}-r2`,
        title: 'Forbind',
        purpose: 'Kombinér med det du kan i forvejen.',
        mode: 'guided',
        generate: literal(p.connect),
      }),
      r3,
      round({
        id: `${id}-r4`,
        title: 'Blanding',
        purpose: 'Ingen fast rækkefølge.',
        mode: 'recall',
        generate: literal(p.recall2),
      }),
      round({
        id: `${id}-r5`,
        title: 'Mini-udfordring',
        purpose: 'Uden ledetråde.',
        mode: 'challenge',
        generate: literal(p.challenge),
      }),
    ],
  };
}

export function singleKeyTrin(
  tierId: CurriculumTierId,
  n: number,
  title: string,
  description: string,
  goal: string,
  key: string,
  prior: string[],
  p: PairTrinPhrases,
  opts: PairTrinOptions = {},
): Trin {
  return pairTrin(
    tierId,
    n,
    title,
    description,
    goal,
    [key],
    prior,
    p,
    opts,
  );
}

export interface ConsolidationTrinOptions {
  /** Tilføj diktat-runde før mini-udfordring (default true). */
  dictation?: boolean;
  /** Engelsk ord/sætning når GenerateRoundOptions.niveau4Lexicon === 'en'. */
  lexicon?: {
    wordsEn: string[];
    sentenceEn?: string;
  };
  /** Begræns gentagelser af samme ord inden for én runde. */
  maxRepeatPerChunk?: number;
}

export function consolidationTrin(
  tierId: CurriculumTierId,
  n: number,
  title: string,
  description: string,
  goal: string,
  unlockedKeys: string[],
  words: string[],
  sentence?: string,
  opts: ConsolidationTrinOptions = {},
): Trin {
  const id = `${tierId}-trin-${n}`;
  const letterKeys = unlockedKeys.filter((k) => k !== ' ');
  const withDictation = opts.dictation !== false;
  const wordsEn = opts.lexicon?.wordsEn;
  const chunkOpts = { maxRepeatPerChunk: opts.maxRepeatPerChunk };
  const chunkGen = (chunks: string[], length: number) =>
    wordsEn
      ? localizedFromChunks(chunks, wordsEn, length, chunkOpts)
      : fromChunks(chunks, length, chunkOpts);
  const sentenceGen = sentence
    ? localizedLiteral(sentence, opts.lexicon?.sentenceEn ?? sentence)
    : null;

  const baseRounds = [
    round({
      id: `${id}-r1`,
      title: 'Korte ord',
      purpose: 'Velkendte ord.',
      mode: 'guided',
      generate: chunkGen(words.slice(0, 12), 70),
    }),
    round({
      id: `${id}-r2`,
      title: 'Ord-blanding',
      purpose: 'Varieret ordforråd.',
      mode: 'recall',
      generate: chunkGen(words, 90),
    }),
    ...(sentenceGen
      ? [
          round({
            id: `${id}-r3`,
            title: 'Sætning',
            purpose: 'Kort sætning med mellemrum.',
            mode: 'recall',
            generate: sentenceGen,
          }),
        ]
      : []),
    round({
      id: `${id}-r4`,
      title: 'Blanding',
      purpose: 'Tilfældigt ordvalg.',
      mode: 'recall',
      generate: chunkGen(words, 100),
    }),
    round({
      id: `${id}-r5`,
      title: 'Flow',
      purpose: 'Hold rytmen.',
      mode: 'recall',
      generate: fromLetters(letterKeys, 4, 80),
    }),
  ];

  const dictationRound = withDictation
    ? [
        round({
          id: `${id}-r-dict`,
          title: 'Diktat: lyt og skriv',
          purpose: 'Lyt til ordet og skriv det uden at se det.',
          mode: 'recall',
          generate: chunkGen(words, 60),
          presentation: { kind: 'dictation' },
        }),
      ]
    : [];

  return {
    id,
    tierId,
    trinNumber: n,
    title,
    description,
    goal,
    unlockedKeys,
    newKeys: [],
    rounds: [
      ...baseRounds,
      ...dictationRound,
      round({
        id: `${id}-r6`,
        title: 'Mini-udfordring',
        purpose: 'Uden hjælp.',
        mode: 'challenge',
        generate: chunkGen(words, 90),
      }),
    ],
  };
}
